import {openConfirmationModalWithCallback, openEditModal, sendCommand, sendQuery} from "../../common/utils";
import {
  DeclareDischargeList,
  GetDischargeListAsCsv,
  IE3ContainerProcessSummary,
  IE3GetDischargeDifferences,
  IE3GetDischargeDifferencesResult,
  IE3RefuseDischargeOverlanded,
  IE3TransportEquipment
} from "@portbase/bezoekschip-service-typescriptmodels";
import {exportDataAsExcel} from "../../common/upload/excel.utils";
import {AppContext} from "../../app-context";
import moment from "moment";
import {ComparatorChain} from "../../common/comparator-chain";
import {PortvisitUtils} from "../../refdata/portvisit-utils";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../../common/modal/modal-confirm.component";
import {ModalErrorAutofocus, ModalErrorAutofocusData} from "../../common/modal-error/modal-error.component";
import {
  MasterConsignmentDetailsComponent,
  MasterConsignmentDetailsComponentData
} from "../consignments/details/master-consignment-details/master-consignment-details.component";

export class ContainerUtils {

  static constructContainerProcessId = (crn: string, equipmentNumber: string) =>
    !crn || !equipmentNumber ? null : `${crn}_${equipmentNumber}`;

  static statusFormatter = (value: String) => {
    switch (value) {
      case "EXPECTED":
        return "Expected";
       case "Dischared":
        return "Discharged";
      case null:
        return "";
    }
  }

  static downloadDischargeList(crn: string, cargoDeclarantShortName? : string, terminalCode?: string) {
    sendQuery("com.portbase.bezoekschip.common.api.cargo.GetDischargeListAsCsv", <GetDischargeListAsCsv>{
      crn: crn,
      cargoDeclarantShortName: cargoDeclarantShortName,
      terminalCode: terminalCode
    }, {responseType: 'text'}).subscribe(csv => {
      const data: string[][] = [];
      csv.split('\n').forEach(line => data.push(line.split(';').map(s => s.replace(/"+/g, ''))));
      exportDataAsExcel(data, 'dischargeList-' + crn + (terminalCode ? '-' + terminalCode : '') + '.xlsx');
    })
  }

  static downloadDischargeDifferences(crn: string) {
    sendQuery("com.portbase.bezoekschip.common.api.consignments.containers.queries.GetDischargeDifferences", <IE3GetDischargeDifferences>{
      crn: crn
    }).subscribe((result: IE3GetDischargeDifferencesResult[]) => this.mapDischargeDifferences(crn, result))
  }

  static sendDischargeListToTerminal(crn: string, cargoDeclarantShortName? : string, terminalCode?: string) {
    sendCommand("com.portbase.bezoekschip.common.api.cargo.DeclareDischargeList",
      <DeclareDischargeList>{
        crn: crn,
        cargoDeclarantShortName: cargoDeclarantShortName,
        terminalCodes: [terminalCode]
      }, () => {
        AppContext.registerSuccess("Discharge list sent successfully");
      })
  }

  private static mapDischargeDifferences(crn: string, containers: IE3GetDischargeDifferencesResult[]) {
    const result = [["Call reference nr", "Cargo handling agent", "Container nr", "Shortlanded", "Overlanded", "Empty", "Discharge date"]];
    const rows: any[] = containers.map(c => [
      crn,
      c.cargoHandlingAgent || "Unknown",
      c.containerNumber,
      c.shortLanded ? "Y" : "N",
      c.overLanded ? "Y" : "N",
      c.empty ? "Y" : "N",
      c.dischargeDate
        ? moment(c.dischargeDate).format('DD-MM-YYYY HH:mm')
        : PortvisitUtils.dischargeResultTerminals.includes(c.terminalShortName)
          ? null : "Not expected to receive discharge date from terminal"]);
    const byAgent = (a, b) => a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0;
    const byContainerNr = (a, b) => a[2] < b[2] ? -1 : a[2] > b[2] ? 1 : 0;
    rows.sort(new ComparatorChain(byAgent, byContainerNr).compare);
    rows.forEach(r => result.push(r));
    exportDataAsExcel(result, crn + '_dischargeDifferences.xlsx');
  }

  static refuseOverlandedContainers = (selectedContainers: IE3ContainerProcessSummary[]) =>
    openConfirmationModalWithCallback((confirmed) => {
      if (confirmed) {
        selectedContainers.forEach(containerProcess => {
          sendCommand('com.portbase.bezoekschip.common.api.consignments.containers.commands.RefuseDischargeOverlanded',
            <IE3RefuseDischargeOverlanded>{
              containerNumber: containerProcess.containerNumber,
              crn: containerProcess.crn
            },
            () => AppContext.registerSuccess(selectedContainers.length + ' overlanded containers have been refused successfully.')
          );
        });
      }
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: "danger",
      title: "Refuse containers",
      message: "You are about to refuse " + selectedContainers.length + " overlanded containers",
      question: "Are you sure that you want to refuse these overlanded containers?",
      confirmText: "Yes",
      cancelText: "No"
    }, 'static');

  static createConsignment = (selectedContainers: IE3ContainerProcessSummary[]) => {
    const crnList = selectedContainers
      .map(c => c.crn)
      .reduce((acc, current) => {
        if (!acc.includes(current)) {
          acc.push(current);
        }
        return acc;
      }, [] as string[]);
    if (crnList.length > 1) {
      this.showError();
    } else {
      openEditModal(MasterConsignmentDetailsComponent, <MasterConsignmentDetailsComponentData>{
        crn: crnList[0],
        consignmentProcessId: null,
        filingType: null,
        transportEquipmentMap: this.summaryToTransportEquipment(selectedContainers),
        cachedConsignmentProcess: null
      })
    }
  };

  private static summaryToTransportEquipment(selectedContainers: IE3ContainerProcessSummary[]):
    { [containerIdentificationNumber: string]: IE3TransportEquipment } {
    const transportEquipment = {};

    selectedContainers.forEach(s => {
      const containerNumber = s.containerNumber;

      if (!!containerNumber) {
        const trimmedContainerNumber = containerNumber.trim();

        transportEquipment[trimmedContainerNumber] = <IE3TransportEquipment>{
          containerIdentificationNumber: trimmedContainerNumber,
        };
      }
    })

    return transportEquipment;
  }

  private static showError = () => {
    openConfirmationModalWithCallback((confirmed) => {
      if (confirmed) {
        // Do nothing
      }
    }, ModalErrorAutofocus, <ModalErrorAutofocusData>{
      type: "warning",
      title: "Not allowed",
      message: "It is not allowed to add containers from more than one visit in one consignment",
    }, 'static');
  }
}
